import { push } from 'svelte-spa-router';
import { api, type ApiResponse } from './client';

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  full_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone?: string;
}

// Role-based registration interfaces
interface EmployeeRegistrationData {
  full_name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  terms_and_conditions:boolean
}

interface CharityRegistrationData {
  name: string;
  email: string;
  category_uuid: string;
  /** DGR Item 1 or Item 2 only — required for workplace giving eligibility. */
  dgr_tax_status: 'dgr1' | 'dgr2';
  /** Charity ABN (11 digits). */
  charity_registration_number: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  password: string;
  password_confirmation: string;
  head_office_location?: string;
  website?: string;
  description?: string;
  terms_and_conditions:boolean
}

interface EmployerRegistrationData {
  name: string;
  email: string;
  abn: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  password: string;
  password_confirmation: string;
  head_office_location?: string;
  terms_and_conditions:boolean
}

interface OtpData {
  otp_code: string;
}

interface ResendOtpData {
  email: string;
}

interface UpdateEmailData {
  email: string;
}

interface ConfirmEmailUpdateData {
  token: string;
  new_email?: string;
}

interface UpdateProfileData {
  full_name?: string;
  phone?: string;
  job_title?: string;
  department?: string;
  location?: string;
}

export interface User {
  id?: number;
  uuid: string;
  email: string;
  full_name: string;
  email_verified_at?: string;
  phone?: string;
  user_role?: {
    uuid: string;
    name: string;
  };
  [key: string]: any; // Allow other properties
}

export const AuthService = {
  /**
   * Log in a user
   */
  login: async (credentials: LoginCredentials) => {
    try {
      const response = await api.post<ApiResponse<{ token: string; user: User }>>('/user/login', credentials);
      const { token, user } = response.data.data;
      
      // Store the token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      push("/")
      return { user, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },
  
  /**
   * Register a new user (legacy method for backward compatibility)
   */
  register: async (userData: RegisterData) => {
    try {
      const response = await api.post<ApiResponse<{ token: string; user: User }>>('/user/signup', userData);
      const { token, user } = response.data.data;
      
      // Store the token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userEmail', userData.email);

      return { user, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Register a new employee
   */
  registerEmployee: async (userData: EmployeeRegistrationData) => {
    try {
      const response = await api.post<ApiResponse<{ user: User }>>('/register/employee', userData);
      const { user } = response.data.data;
      
      // Store user email for OTP verification
      localStorage.setItem('userEmail', userData.email);

      return { user, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Register a new charity
   */
  registerCharity: async (userData: CharityRegistrationData, logo?: File | null) => {
    try {
      let response;
      if (logo) {
        const formData = new FormData();
        Object.entries(userData).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }
          if (key === 'terms_and_conditions') {
            formData.append(key, value ? '1' : '0');
            return;
          }
          formData.append(key, String(value));
        });
        formData.append('logo', logo);
        response = await api.post<ApiResponse<{ charity: any; user: User }>>(
          '/register/charity',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        response = await api.post<ApiResponse<{ charity: any; user: User }>>('/register/charity', userData);
      }
      const { user } = response.data.data;
      
      // Store user email for OTP verification
      localStorage.setItem('userEmail', userData.email);

      return { user, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Register a new employer
   */
  registerEmployer: async (userData: EmployerRegistrationData) => {
    try {
      const response = await api.post<ApiResponse<{ employer: any; user: User }>>('/register/employer', userData);
      const { user } = response.data.data;
      
      // Store user email for OTP verification
      localStorage.setItem('userEmail', userData.email);

      return { user, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Verify OTP code
   */
  verifyOtpEmail: async (otpData: OtpData & { email: string }) => {
    try {
      const response = await api.post<ApiResponse<{ token: string; user: User & { verified: boolean } }>>('/verify/email', otpData);
  
      const { token, user } = response.data.data;
      
      // Store the token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      return { user, success: user.verified || response.data.success };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Resend OTP code
   */
  resendOtpEmail: async (resendOtpData: ResendOtpData) => {
    try {
      const response = await api.post<ApiResponse<{ sent: boolean }>>('/verify/resend', resendOtpData);
      return { success: response.data.data.sent };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Verify OTP code
   */
  verifyOtp: async (otpData: OtpData) => {
    try {
      const response = await api.post<ApiResponse<{ token: string; user: User, verified: boolean }>>('/user/otpcode', otpData);

      const { verified, user } = response.data.data;
      
      // Store the user in localStorage
      localStorage.setItem('user', JSON.stringify(user));

      return { user, success: verified };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },
  
  /**
   * Resend OTP code
   */
  resendOtp: async (resendOtpData: ResendOtpData) => {
    try {
      const response = await api.post<ApiResponse<{ sent: boolean }>>('/user/resend-otpcode', resendOtpData);
      return { success: response.data.data.sent };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },
  
  /**
   * Log out the current user
   */
  logout: async () => {
    try {
      await api.get('/user/logout');
      
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userEmail');
      push("/login")
      return { success: true };
    } catch (error) {
      // Still clear tokens on error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userEmail');
      
      return { success: false, error };
    }
  },
  
  /**
   * Update user email
   */
  updateEmail: async (emailData: UpdateEmailData) => {
    try {
      const response = await api.post<ApiResponse<{ user: User }>>('/user/change-email', emailData);
      const { user } = response.data.data;
      
      // Update user in localStorage
      localStorage.setItem('user', JSON.stringify(user));
      
      return { user, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Confirm email update with token
   */
  confirmEmailUpdate: async (confirmData: ConfirmEmailUpdateData) => {
    try {
      const response = await api.get<ApiResponse<{ user: User }>>('/user/verify-email-change', {
        params: {
          token: confirmData.token,
        }
      });
      const user = response.data.data?.user;
      
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return { user, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Reset password - request password reset email
   */
  resetPassword: async (data: { email: string }) => {
    try {
      const response = await api.post<ApiResponse<{ message: string }>>('/user/reset-password', data);
      return { 
        success: true, 
        message: response.data.data?.message || response.data.message || 'Password reset email sent successfully'
      };
    } catch (error: any) {
      return { 
        success: false, 
        error,
        message: error?.response?.data?.message || 'Failed to send password reset email'
      };
    }
  },

  /**
   * Update password via reset token
   */
  updatePasswordViaReset: async (data: {
    token: string;
    password: string;
    password_confirmation: string;
  }) => {
    try {
      const response = await api.put<ApiResponse<{ message: string }>>('/user/update-password', data);
      return {
        success: true,
        message: response.data.data?.message || response.data.message || 'Password updated successfully'
      };
    } catch (error: any) {
      const apiData = error?.response?.data;
      return {
        success: false,
        error,
        message: apiData?.message || 'Failed to update password',
        errors: apiData?.data?.errors ?? apiData?.data ?? undefined,
      };
    }
  },

  /**
   * Update user profile
   */
  updateProfile: async (profileData: UpdateProfileData) => {
    try {
      const user = AuthService.getUser();
      if (!user?.uuid) {
        return { success: false, error: 'User not authenticated' };
      }

      const response = await api.put<ApiResponse<{ user: User }>>(`/user/${user.uuid}`, profileData);
      const { user: updatedUser } = response.data.data;
      
      // Update user in localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return { user: updatedUser, success: true };
    } catch (error: unknown) {
      return { success: false, error };
    }
  },

  /**
   * Get the current authenticated user
   */
  getCurrentUser: async () => {
    try {
      // Get user from localStorage first
      const user = AuthService.getUser();
      
      if (!user) {
        return { success: false, error: 'User not found' };
      }
      
      if (!user.uuid) {
        return { success: false, error: 'User UUID missing' };
      }
      
      // Get fresh user data from API (org/charity fields e.g. bank_details_status must stay current)
      try {
        const response = await api.get<ApiResponse<User>>(`/user/${user.uuid}`);
        const freshUser = response.data.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        return { user: freshUser, success: true };
      } catch (apiError) {
        // If API call fails, but we have local user data, still consider it a success
        return { user, success: true };
      }
    } catch (error) {
      return { success: false, error };
    }
  },
  
  /**
   * Check if user is authenticated
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
  
  /**
   * Get the current user from localStorage
   */
  getUser: (): User | null => {
    try {
      const userJson = localStorage.getItem('user');
      
      if (!userJson || userJson === "undefined") {
        return null;
      }
      
      return JSON.parse(userJson);
    } catch (error) {
      localStorage.removeItem('user');
      return null;
    }
  },
};

// Export the registration data types for use in components
export type { EmployeeRegistrationData, CharityRegistrationData, EmployerRegistrationData, UpdateProfileData }; 